import React, { type ReactNode, useCallback, useRef } from 'react';
import { Checkbox as CheckboxRAC } from 'react-aria-components';

import { useFocusFieldEvent } from '../../contexts/events-context';
import { useFieldAttributes } from '../../contexts/field-attributes-context';
import { useFieldPropsV2 } from '../../contexts/field-props-context';
import { findChildOfType } from '../../services/find-child';
import { Description } from '../field-description/field-description-headless';
import { Error } from '../field-error/field-error-headless';
import { RichTextLabel } from '../field-rich-label/field-rich-label-headless';

export type CheckboxProps = {
  children: ReactNode;
};

export const Checkbox = ({ children }: CheckboxProps) => {
  const { id, FieldLayout } = useFieldPropsV2();

  const checkboxElement = findChildOfType(children, Checkbox.Checkbox);
  const descriptionElement = findChildOfType(children, Checkbox.Description);
  const errorElement = findChildOfType(children, Checkbox.Error);

  return (
    <FieldLayout
      fieldId={id}
      renderLabel={() => null}
      renderInput={() => checkboxElement}
      renderDescription={() => (
        <>
          {descriptionElement}
          {errorElement}
        </>
      )}
    />
  );
};

type CheckboxContainerProps = {
  children: ReactNode;
  className?: string;
};

const CheckboxContainer = ({ children, className }: CheckboxContainerProps) => {
  const { id, value, onChange, disabled, required, onBlur, onFocus, hasError } =
    useFieldPropsV2();
  const { inputId, ariaDescribedBy } = useFieldAttributes();

  const ref = useRef<HTMLLabelElement>(null);
  useFocusFieldEvent(() => {
    ref.current?.focus();
  }, id);

  const labelElement = findChildOfType(
    children,
    CheckboxContainer.RichTextLabel,
  );
  const inputElement = findChildOfType(children, CheckboxContainer.Input);

  const handleChange = useCallback(
    (isSelected: boolean) => {
      onChange(isSelected);
    },
    [onChange],
  );

  return (
    <CheckboxRAC
      ref={ref}
      id={inputId}
      aria-describedby={ariaDescribedBy}
      isSelected={Boolean(value)}
      onChange={handleChange}
      isDisabled={disabled}
      isRequired={required}
      isInvalid={hasError}
      onBlur={onBlur}
      onFocus={onFocus}
      className={className}
    >
      {() => (
        <>
          {inputElement}
          {labelElement}
        </>
      )}
    </CheckboxRAC>
  );
};

type InputProps = {
  className?: string;
};

const Input = ({ className }: InputProps) => {
  return <div aria-hidden="true" className={className} />;
};

CheckboxContainer.RichTextLabel = RichTextLabel;
CheckboxContainer.Input = Input;

Checkbox.Checkbox = CheckboxContainer;
Checkbox.Description = Description;
Checkbox.Error = Error;
